
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { FunnelData } from "../types";
import { colorSchemes, proColorSchemes } from "../constants";
import { cn } from "@/lib/utils";
import { LogoDisplay } from "./LogoDisplay";

interface PreviewHeaderProps {
  data: FunnelData;
  onPageChange?: (slug: string) => void;
  isMobileView?: boolean;
}

export const PreviewHeader = ({ data, onPageChange, isMobileView = false }: PreviewHeaderProps) => {
  const colors = (data.isPro && data.theme in proColorSchemes) 
    ? proColorSchemes[data.theme as keyof typeof proColorSchemes]
    : (data.theme in colorSchemes)
      ? colorSchemes[data.theme as keyof typeof colorSchemes]
      : colorSchemes.slateLight;

  const menuPages = data.pages.filter(page => page.showInMenu);

  const handlePageClick = (page: { slug: string, isMainPage?: boolean }) => {
    // Always update URL for proper routing state
    const currentUrl = new URL(window.location.href);
    
    if (page.isMainPage) {
      if (currentUrl.pathname.startsWith('/f/')) {
        const pathParts = currentUrl.pathname.split('/');
        if (pathParts.length >= 3) {
          history.pushState({}, '', `/f/${pathParts[2]}`);
        }
      } else if (currentUrl.pathname.startsWith('/funnels/')) {
        // If we're in the builder preview, update the URL without the page slug
        const builderPath = currentUrl.pathname.split('/').slice(0, 3).join('/');
        history.pushState({}, '', builderPath);
      } else {
        // For subdomain or custom domain, use origin
        history.pushState({}, '', window.location.origin);
      }
    } else {
      if (currentUrl.pathname.startsWith('/f/')) {
        const pathParts = currentUrl.pathname.split('/');
        if (pathParts.length >= 3) {
          history.pushState({}, '', `/f/${pathParts[2]}/${page.slug}`);
        }
      } else if (currentUrl.pathname.startsWith('/funnels/')) {
        // If we're in the builder preview, append the page slug
        const builderPath = currentUrl.pathname.split('/').slice(0, 3).join('/');
        history.pushState({}, '', `${builderPath}/${page.slug}`);
      } else {
        history.pushState({}, '', `/${page.slug}`);
      }
    }

    // Call the onPageChange handler if provided
    if (onPageChange) {
      onPageChange(page.isMainPage ? "" : page.slug);
    } else {
      // If no handler provided, reload the page to the new URL
      window.location.reload();
    }
  };

  return (
    <header 
      data-header
      className={cn(
        "sticky top-0 w-full z-50 transition-all duration-200",
        "bg-white/80 backdrop-blur-sm border-b border-slate-200/60"
      )}
    >
      <div className="container max-w-7xl mx-auto py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-12">
            <LogoDisplay data={data} colors={colors} />

            {menuPages.length > 0 && !isMobileView && (
              <div className="flex items-center gap-8">
                {menuPages.map((page) => (
                  <button
                    key={page.id}
                    onClick={() => handlePageClick(page)}
                    className="text-sm lg:text-[15px] font-medium py-2.5 px-4 xl:px-5 rounded-full transition-colors hover:bg-slate-100"
                    style={{ color: colors.primary }}
                  >
                    {page.title || 'Untitled Page'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {menuPages.length > 0 && isMobileView && (
            <div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    style={{ color: colors.primary }}
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end"
                  className="w-56 bg-white/80 backdrop-blur-sm border-slate-200/60"
                >
                  {menuPages.map((page) => (
                    <DropdownMenuItem
                      key={page.id}
                      onClick={() => handlePageClick(page)}
                      className="cursor-pointer"
                      style={{ color: colors.primary }}
                    >
                      {page.title || 'Untitled Page'}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
